import React, { useEffect, useState } from 'react';
import taskAPI from '../api/taskAPI';
import workerAPI from '../api/workerAPI';
import UserDashboard from './UserDashboard';
import {
  FaFileAlt, FaTimes, FaChevronRight, FaLink,
  FaFilePdf, FaFileWord, FaFileImage, FaFile,
  FaDownload, FaEye, FaCheckCircle, FaSearch,
  FaCheck,
} from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';

// ── Helpers ───────────────────────────────────────────────────────────────────
const ROLE_COLOR = {
  'Marketing':     'bg-pink-100 text-pink-700',
  'Project Leads': 'bg-indigo-100 text-indigo-700',
  'HR & Design':   'bg-teal-100 text-teal-700',
  'TL':            'bg-blue-100 text-blue-700',
  'Developer':     'bg-green-100 text-green-700',
  'Office':        'bg-gray-100 text-gray-600',
  'admin':         'bg-purple-100 text-purple-700',
};
const RoleBadge = ({ role }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-600'}`}>{role}</span>
);
const StatusBadge = ({ status }) => {
  const s =
    status === 'Completed'   ? 'bg-green-100 text-green-700' :
    status === 'In Progress' ? 'bg-blue-100 text-blue-700'   :
                               'bg-yellow-100 text-yellow-700';
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s}`}>{status}</span>;
};
const ChainTrail = ({ chain }) => {
  if (!chain?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {chain.map((entry, i) => (
        <React.Fragment key={i}>
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">{entry}</span>
          {i < chain.length - 1 && <FaChevronRight className="text-purple-300 text-xs" />}
        </React.Fragment>
      ))}
    </div>
  );
};
const FileIcon = ({ fileType, className = 'text-xl' }) => {
  if (fileType === 'image') return <FaFileImage className={`text-blue-500 ${className}`} />;
  if (fileType === 'pdf')   return <FaFilePdf   className={`text-red-500  ${className}`} />;
  if (fileType === 'doc')   return <FaFileWord  className={`text-blue-700 ${className}`} />;
  return <FaFile className={`text-gray-500 ${className}`} />;
};

// Roles that can acknowledge
const ACK_ROLES = ['Office', 'HR & Design', 'Project Leads'];

// ─────────────────────────────────────────────────────────────────────────────
const AdminTaskView = () => {
  const worker   = JSON.parse(localStorage.getItem('worker'));
  // Safe JWT decode — handles padding + tries both token key names
  const decodeJWT = (key) => {
    try {
      const t = localStorage.getItem(key);
      if (!t) return null;
      const base64 = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    } catch { return null; }
  };
  const decoded = decodeJWT('token') || decodeJWT('adminToken');
  const isAdmin  = !!decoded?.adminId;
  const viewerRole = worker?.role || 'admin';

  // Current user's ID — for checking if they acknowledged
  const currentUserId = String(decoded?.adminId || decoded?.workerId || worker?._id || '');

  const [tasks, setTasks]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [reportFilter, setReportFilter] = useState('All');

  // ── Acknowledger workers list (Office + HR & Design + Project Leads) ──────
  const [ackWorkers, setAckWorkers]     = useState([]); // [{_id, username, role}]

  // ── Reports modal ─────────────────────────────────────────────────────────
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [selectedTask, setSelectedTask]         = useState(null);
  const [taskReports, setTaskReports]           = useState([]);
  const [loadingReports, setLoadingReports]     = useState(false);
  const [previewUrl, setPreviewUrl]             = useState(null);

  // ── Status edit ───────────────────────────────────────────────────────────
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [newStatus, setNewStatus]             = useState('');
  const [savingStatus, setSavingStatus]       = useState(false);

  // ── Chain modal ───────────────────────────────────────────────────────────
  const [showChainModal, setShowChainModal] = useState(false);
  const [chainTask, setChainTask]           = useState(null);

  // ── Ack modal (per task full list) ────────────────────────────────────────
  const [showAckModal, setShowAckModal] = useState(false);
  const [ackTask, setAckTask]           = useState(null);
  const [togglingId, setTogglingId]     = useState(null); // workerId being toggled

  useEffect(() => {
    fetchTasks();
    fetchAckWorkers();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await taskAPI.get('/');
      setTasks(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Fetch all workers who can acknowledge (Office, HR & Design, Project Leads)
  const fetchAckWorkers = async () => {
    try {
      const res = await workerAPI.get('/');
      const filtered = res.data.filter(w => ACK_ROLES.includes(w.role));
      setAckWorkers(filtered);
    } catch (err) { console.error(err); }
  };

  // ── Toggle acknowledgement ────────────────────────────────────────────────
  const toggleAck = async (taskId, workerId) => {
    setTogglingId(workerId);
    try {
      const res = await taskAPI.patch(`/${taskId}/acknowledge`, {});
      // Update task in list
      setTasks(prev => prev.map(t =>
        t._id === taskId ? { ...t, acknowledgements: res.data.acknowledgements } : t
      ));
      // Update ack modal task if open
      if (ackTask?._id === taskId) {
        setAckTask(prev => ({ ...prev, acknowledgements: res.data.acknowledgements }));
      }
    } catch (err) {
      alert(err.response?.data?.error || '❌ Failed to toggle acknowledgement.');
    } finally { setTogglingId(null); }
  };

  // Check if a specific worker has acknowledged
  // Compare as strings — handles ObjectId vs string mismatch
  const hasAcknowledged = (task, userId) => {
    if (!userId || !task.acknowledgements?.length) return false;
    const uid = String(userId);
    return task.acknowledgements.some(a => String(a.userId) === uid);
  };

  // ── Reports modal ─────────────────────────────────────────────────────────
  const openReports = async (task) => {
    setSelectedTask(task); setTaskReports([]); setShowReportsModal(true);
    setLoadingReports(true);
    try {
      const res = await taskAPI.get(`/task/${task._id}`);
      setTaskReports(res.data);
    } catch (err) { console.error(err); }
    finally { setLoadingReports(false); }
  };

  // ── Status update ─────────────────────────────────────────────────────────
  const saveStatus = async (taskId) => {
    setSavingStatus(true);
    try {
      const res = await taskAPI.patch(`/${taskId}/status`, { status: newStatus });
      setTasks(prev => prev.map(t => t._id === taskId ? { ...t, status: res.data.task.status } : t));
      setEditingStatusId(null);
      alert('✅ Status updated!');
    } catch (err) {
      alert(err.response?.data?.error || '❌ Failed to update status.');
    } finally { setSavingStatus(false); }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const filtered = tasks.filter(t => {
    const matchSearch =
      t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.assignees?.some(a => a.username?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      t.createdBy?.username?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'All' || t.status === statusFilter;
    const matchReport =
      reportFilter === 'All'       ? true :
      reportFilter === 'HasReport' ? (t.reportCount > 0 || t.reports?.length > 0) :
                                     (!t.reportCount && !t.reports?.length);
    return matchSearch && matchStatus && matchReport;
  });

  // ── Can current user acknowledge ──────────────────────────────────────────
  const canAck = isAdmin || ACK_ROLES.includes(viewerRole);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-6 pt-20">
      {isAdmin ? null : <UserDashboard />}

      {/* Header */}
      <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center mb-6 mt-3 gap-3">
        <div>
          <h2 className="text-3xl font-bold font-mono text-purple-700">Task Overview</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {isAdmin ? 'Admin Panel' : <>{worker?.username} <RoleBadge role={viewerRole} /></>}
            {' · '}{filtered.length} task(s)
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {['All', 'Pending', 'In Progress', 'Completed'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                statusFilter === s ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'
              }`}>{s}</button>
          ))}
          <select value={reportFilter} onChange={e => setReportFilter(e.target.value)}
            className="border px-3 py-1.5 rounded-full text-sm text-gray-600 outline-none">
            <option value="All">All Reports</option>
            <option value="HasReport">Has Report</option>
            <option value="NoReport">No Report</option>
          </select>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-7xl mx-auto mb-4">
        <div className="relative w-full md:w-96">
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
          <input type="text" placeholder="Search by title, assignee, creator..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="border pl-10 pr-4 py-2 rounded-lg w-full text-sm focus:ring-2 focus:ring-purple-400 outline-none" />
        </div>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-lg overflow-x-auto">
        <div className="overflow-y-auto max-h-[600px]">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-purple-100 text-purple-700 sticky top-0 z-10">
              <tr>
                <th className="py-3 px-4 text-left">Date</th>
                <th className="py-3 px-4 text-left">Deadline</th>
                <th className="py-3 px-4 text-left">Title</th>
                <th className="py-3 px-4 text-left">Created By</th>
                <th className="py-3 px-4 text-left">Assigned To</th>
                <th className="py-3 px-4 text-left">Status</th>
                <th className="py-3 px-4 text-left">Chain</th>
                {/* ── NEW: Acknowledged by ── */}
                <th className="py-3 px-4 text-left">Acknowledged</th>
                <th className="py-3 px-4 text-left">Reports</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 divide-y divide-gray-100">
              {loading && <tr><td colSpan="9" className="text-center py-12 text-gray-400">Loading tasks…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan="9" className="text-center py-12 text-gray-400">No tasks found.</td></tr>}

              {filtered.map(task => (
                <tr key={task._id} className="hover:bg-purple-50 transition">

                  <td className="py-3 px-4 whitespace-nowrap text-xs text-gray-500">
                    {task.today ? new Date(task.today).toLocaleDateString('en-GB') : new Date(task.createdAt).toLocaleDateString('en-GB')}
                  </td>

                  <td className="py-3 px-4 whitespace-nowrap text-xs text-gray-500">
                    {task.deadline ? new Date(task.deadline).toLocaleDateString('en-GB') : '—'}
                  </td>

                  <td className="py-3 px-4">
                    <p className="font-semibold text-gray-800">{task.title}</p>
                    {task.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{task.description}</p>}
                  </td>

                  <td className="py-3 px-4">
                    {task.createdBy?.username ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-xs font-medium text-gray-700">{task.createdBy.username}</span>
                        <RoleBadge role={task.createdBy.role} />
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>

                  <td className="py-3 px-4">
                    {task.assignees?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {task.assignees.map((a, i) => (
                          <span key={i} className="flex items-center gap-1 text-xs bg-gray-50 border rounded-full px-2 py-0.5">
                            {a.username} <RoleBadge role={a.role} />
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>

                  {/* Status */}
                  <td className="py-3 px-4">
                    {isAdmin && editingStatusId === task._id ? (
                      <div className="flex items-center gap-2">
                        <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                          className="border rounded px-2 py-1 text-xs focus:ring-2 focus:ring-purple-400 outline-none">
                          <option value="Pending">Pending</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                        </select>
                        <button onClick={() => saveStatus(task._id)} disabled={savingStatus}
                          className="text-green-600 hover:text-green-800 text-sm disabled:opacity-50"><FaCheckCircle /></button>
                        <button onClick={() => setEditingStatusId(null)} className="text-red-400 hover:text-red-600 text-sm"><FaTimes /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <StatusBadge status={task.status} />
                        {isAdmin && (
                          <button onClick={() => { setEditingStatusId(task._id); setNewStatus(task.status); }}
                            className="text-gray-400 hover:text-purple-600 text-sm"><MdEdit /></button>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Chain */}
                  <td className="py-3 px-4">
                    {task.assignmentChain?.length > 0 ? (
                      <button onClick={() => { setChainTask(task); setShowChainModal(true); }}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                        <FaLink /> View
                      </button>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>

                  {/* ── Acknowledged column ─────────────────────────────── */}
                  <td className="py-3 px-4">
                    <div className="flex flex-col gap-1 min-w-[140px]">
                      {/* Show all ack-eligible workers with checkbox */}
                      {ackWorkers.map(w => {
                        const acked     = hasAcknowledged(task, w._id);
                        const isSelf    = w._id?.toString() === currentUserId?.toString();
                        const canToggle = canAck && (isAdmin || isSelf);
                        const isToggling = togglingId === w._id;

                        return (
                          <label key={w._id}
                            className={`flex items-center gap-2 text-xs cursor-pointer group ${canToggle ? '' : 'cursor-default'}`}
                            title={canToggle ? (acked ? 'Click to un-acknowledge' : 'Click to acknowledge') : 'You cannot acknowledge on behalf of others'}>
                            <input
                              type="checkbox"
                              checked={acked}
                              disabled={!canToggle || isToggling}
                              onChange={() => canToggle && toggleAck(task._id, w._id)}
                              className="accent-purple-600 w-3.5 h-3.5 cursor-pointer disabled:cursor-default"
                            />
                            <span className={`truncate max-w-[80px] ${acked ? 'text-green-700 font-semibold' : 'text-gray-500'}`}>
                              {w.username}
                            </span>
                            <RoleBadge role={w.role} />
                            {acked && <FaCheck className="text-green-500 text-[10px] shrink-0" />}
                          </label>
                        );
                      })}

                      {/* Admin self-acknowledge */}
                      {isAdmin && (
                        <label className="flex items-center gap-2 text-xs cursor-pointer"
                          title={hasAcknowledged(task, currentUserId) ? 'Click to un-acknowledge' : 'Click to acknowledge'}>
                          <input
                            type="checkbox"
                            checked={hasAcknowledged(task, currentUserId)}
                            onChange={() => toggleAck(task._id, currentUserId)}
                            className="accent-purple-600 w-3.5 h-3.5 cursor-pointer"
                          />
                          <span className={`truncate max-w-[80px] ${hasAcknowledged(task, currentUserId) ? 'text-green-700 font-semibold' : 'text-gray-500'}`}>
                            {decoded?.username || 'Admin'}
                          </span>
                          <RoleBadge role="admin" />
                          {hasAcknowledged(task, currentUserId) && <FaCheck className="text-green-500 text-[10px] shrink-0" />}
                        </label>
                      )}

                      {/* Summary count */}
                      {task.acknowledgements?.length > 0 && (
                        <button
                          onClick={() => { setAckTask(task); setShowAckModal(true); }}
                          className="text-[10px] text-purple-600 hover:text-purple-800 underline text-left mt-0.5">
                          {task.acknowledgements.length} acknowledged
                        </button>
                      )}
                    </div>
                  </td>

                  {/* Reports */}
                  <td className="py-3 px-4">
                    <button onClick={() => openReports(task)}
                      className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition ${
                        (task.reportCount > 0 || task.reports?.length > 0)
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}>
                      <FaFileAlt />
                      {task.reportCount > 0 || task.reports?.length > 0
                        ? `${task.reportCount || task.reports?.length} report(s)`
                        : 'reports'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── ACKNOWLEDGEMENT DETAIL MODAL ──────────────────────────────────────── */}
      {showAckModal && ackTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative max-h-[80vh] overflow-y-auto">
            <button onClick={() => setShowAckModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Acknowledgements</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{ackTask.title}</span>"</p>

            {/* All eligible workers with status */}
            <div className="space-y-2 mb-4">
              <p className="text-xs text-gray-400 uppercase font-medium mb-2">Reviewers</p>
              {ackWorkers.map(w => {
                const acked     = hasAcknowledged(ackTask, w._id);
                const isSelf    = w._id?.toString() === currentUserId?.toString();
                const canToggle = canAck && (isAdmin || isSelf);
                const ackEntry  = ackTask.acknowledgements?.find(a => a.userId?.toString() === w._id?.toString());

                return (
                  <div key={w._id} className={`flex items-center gap-3 p-2 rounded-lg border ${acked ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <input
                      type="checkbox"
                      checked={acked}
                      disabled={!canToggle || togglingId === w._id}
                      onChange={() => canToggle && toggleAck(ackTask._id, w._id)}
                      className="accent-purple-600 w-4 h-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`text-sm font-medium ${acked ? 'text-green-700' : 'text-gray-700'}`}>{w.username}</span>
                        <RoleBadge role={w.role} />
                      </div>
                      {acked && ackEntry?.acknowledgedAt && (
                        <p className="text-xs text-gray-400">
                          ✓ {new Date(ackEntry.acknowledgedAt).toLocaleString('en-GB')}
                        </p>
                      )}
                    </div>
                    {acked && <FaCheck className="text-green-500 shrink-0" />}
                  </div>
                );
              })}

              {/* Admin row in modal */}
              {isAdmin && (() => {
                const acked     = hasAcknowledged(ackTask, currentUserId);
                const ackEntry  = ackTask.acknowledgements?.find(a => a.userId?.toString() === currentUserId?.toString());
                return (
                  <div className={`flex items-center gap-3 p-2 rounded-lg border ${acked ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <input type="checkbox" checked={acked}
                      disabled={togglingId === currentUserId}
                      onChange={() => toggleAck(ackTask._id, currentUserId)}
                      className="accent-purple-600 w-4 h-4" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`text-sm font-medium ${acked ? 'text-green-700' : 'text-gray-700'}`}>{decoded?.username || 'Admin'}</span>
                        <RoleBadge role="admin" />
                      </div>
                      {acked && ackEntry?.acknowledgedAt && (
                        <p className="text-xs text-gray-400">✓ {new Date(ackEntry.acknowledgedAt).toLocaleString('en-GB')}</p>
                      )}
                    </div>
                    {acked && <FaCheck className="text-green-500 shrink-0" />}
                  </div>
                );
              })()}
            </div>

            <p className="text-xs text-gray-400 text-center">
              {ackTask.acknowledgements?.length || 0} of {ackWorkers.length + (isAdmin ? 1 : 0)} reviewed
            </p>
          </div>
        </div>
      )}

      {/* ── REPORTS MODAL ─────────────────────────────────────────────────────── */}
      {showReportsModal && selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowReportsModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Task Reports</h3>
            <p className="text-sm text-gray-500 mb-1">"<span className="font-medium text-gray-700">{selectedTask.title}</span>"</p>
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <StatusBadge status={selectedTask.status} />
              {selectedTask.deadline && <span className="text-xs text-gray-400">Deadline: {new Date(selectedTask.deadline).toLocaleDateString('en-GB')}</span>}
              {selectedTask.createdBy?.username && <span className="text-xs text-gray-400">By: <span className="font-medium text-gray-600">{selectedTask.createdBy.username}</span></span>}
            </div>
            {selectedTask.assignmentChain?.length > 0 && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Assignment chain:</p>
                <ChainTrail chain={selectedTask.assignmentChain} />
              </div>
            )}
            {loadingReports && <p className="text-center text-gray-400 py-8">Loading reports…</p>}
            {!loadingReports && taskReports.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <FaFileAlt className="mx-auto text-3xl mb-2 text-gray-300" />
                <p>No reports submitted yet.</p>
              </div>
            )}
            <div className="space-y-4">
              {taskReports.map((report, i) => (
                <div key={i} className="border rounded-xl p-4 bg-gray-50">
                  <div className="flex justify-between items-start flex-wrap gap-2 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-purple-700">{report.submittedBy || report.assigneeId || 'Unknown'}</span>
                      {report.submittedByRole && <RoleBadge role={report.submittedByRole} />}
                    </div>
                    <span className="text-xs text-gray-400">{new Date(report.createdAt).toLocaleString('en-GB')}</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{report.description}</p>
                  {report.photo && (
                    <div className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
                      <FileIcon fileType={report.fileType || 'image'} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{report.fileName || 'Attachment'}</p>
                        <p className="text-xs text-gray-400">{report.fileType || 'file'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {(report.fileType === 'image' || !report.fileType) && (
                          <button onClick={() => setPreviewUrl(report.photo)} className="text-blue-500 hover:text-blue-700"><FaEye /></button>
                        )}
                        <a href={report.photo} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700"><FaDownload /></a>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {isAdmin && (
              <div className="mt-6 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Update Task Status (Admin only)</p>
                <div className="flex items-center gap-3">
                  <select defaultValue={selectedTask.status} onChange={e => setNewStatus(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 outline-none">
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                  <button
                    onClick={async () => {
                      if (!newStatus) return;
                      setSavingStatus(true);
                      try {
                        const res = await taskAPI.patch(`/${selectedTask._id}/status`, { status: newStatus });
                        setTasks(prev => prev.map(t => t._id === selectedTask._id ? { ...t, status: res.data.task.status } : t));
                        setSelectedTask(prev => ({ ...prev, status: res.data.task.status }));
                        alert('✅ Status updated!');
                      } catch (err) { alert(err.response?.data?.error || '❌ Failed.'); }
                      finally { setSavingStatus(false); }
                    }}
                    disabled={savingStatus}
                    className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                    {savingStatus ? 'Saving…' : <><FaCheckCircle /> Update Status</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CHAIN MODAL ──────────────────────────────────────────────────────── */}
      {showChainModal && chainTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowChainModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Assignment Chain</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{chainTask.title}</span>"</p>
            <div className="mb-4"><p className="text-xs text-gray-400 mb-2">Delegation path:</p><ChainTrail chain={chainTask.assignmentChain} /></div>
            <div>
              <p className="text-xs text-gray-400 mb-2">All assignees:</p>
              <div className="space-y-2">
                {chainTask.assignees?.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg border bg-gray-50">
                    <span className="text-sm font-medium flex-1">{a.username}</span>
                    <RoleBadge role={a.role} />
                    <span className="text-xs text-gray-400">← {a.assignedBy}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── IMAGE PREVIEW ─────────────────────────────────────────────────────── */}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)} className="absolute -top-10 right-0 text-white text-2xl hover:text-red-400">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTaskView;